// @ts-nocheck
import React, { Component, useEffect, useRef, useState } from 'react';

import UIContext from './UIContext';
import { set, slice, omit } from 'lodash';
import { useAskBotQuery } from 'graph/dist/generated';
import { askBot, subscribeToMessages } from 'graph/queries';
import { gql, useApolloClient } from '@apollo/client';
import { useRiskController } from 'hooks/useRiskController';
import { useStakeholderController } from 'hooks/useStakeholderController';
import { useStakeController } from 'hooks/useStakeController';
import { useIndicatorController } from 'hooks/useIndicatorController';
import { useStorageService, STORAGE_KEYS } from 'services/storage';
import { useTranslation } from 'react-i18next';
import { defaults as RiskDefaults } from 'model/RiskModel';
import { defaults as StakeholderDefaults } from 'model/StakeholderModel';
import { defaults as StakeDefaults } from 'model/StakeModel';
import { defaults as ConstraintDefaults } from 'model/IndicatorModel';
import { calculateOptimalStakeholderPosition } from 'utils/stakeholderPlacementOptimizer';

let content = []
const defaultPreferences = {
	global: {},
	pages: {
		navigator: {
			links: false
		},
		risks: {
			sortOrder: 'ascend',
			sortBy: 'id',
			columns: {
				evaluatedAt: true,
			}
		},
		stakes: {
			sortOrder: 'ascend',
			sortBy: 'id'
		},
		indicators: {
			sortOrder: 'ascend',
			sortBy: 'id'
		},
		fishbone: {
			branchSort: 'startOn'
		}
	}
};

const UIProvider = ({ children }) => {

	const [ready, setReady] = useState(false);
	const apolloClient = useApolloClient();
	const storage = useStorageService();
	const { i18n } = useTranslation();

	// AI
	const [botThreadId, setBotThreadId] = useState(null);
	const [botThreadMessages, setBotThreadMessages] = useState([]);
	const pendingFlushRef = useRef(null);
	const messagesRef = useRef([]);
	useEffect(() => { messagesRef.current = botThreadMessages; }, [botThreadMessages]);

	// Coalesce streaming deltas into at most one render per animation frame
	// to avoid re-rendering the heavy radar/page tree on every token.
	const scheduleFlush = (nextMessages) => {
		messagesRef.current = nextMessages;
		if (pendingFlushRef.current) return;
		pendingFlushRef.current = requestAnimationFrame(() => {
			pendingFlushRef.current = null;
			setBotThreadMessages(messagesRef.current);
		});
	};

	const [maskedZones, setMaskedZones] = useState({});
	const [isTutorialMode, setIsTutorialMode] = useState(false);

	// Mode lecture seule (readers). Le rôle du caller sur le projet ouvert est
	// injecté par la page projet (myRole du getProject). `null` = pas de projet
	// ouvert ou rôle inconnu -> éditable par défaut (n'affecte pas l'owner). Un
	// `reader` verrouille l'UI ; le verrou serveur reste l'autorité (les
	// affordances masquées ne sont qu'un confort UX).
	const [projectRole, setProjectRoleState] = useState(null);
	const setProjectRole = (role) => setProjectRoleState(role || null);
	const isReadOnly = projectRole === 'reader';
	const canEdit = !isReadOnly;

	const [preferences, setPreferences] = useState({
		...defaultPreferences
	});

	const { createRisk } = useRiskController();
	const { createStakeholder } = useStakeholderController();
	const { createStake } = useStakeController();
	const { createIndicator } = useIndicatorController();

	const [selection, setSelection] = useState(null);

	const addBotMessage = async (projectId, scenarioId, screen, message, callback) => {

		const newMessages = [
			...botThreadMessages,
			{
				role: "user", content: message
			}, {
				role: "assistant",
				loading: true,
				content: "",
			}];
		await setBotThreadMessages(newMessages);

		// Send the prior conversation (everything before the new question and the
		// pending empty assistant slot). The lambda is now stateless: it rebuilds
		// the system prompt with fresh project context every turn and uses this
		// history as the message list — no DynamoDB round-trip.
		const history = botThreadMessages
			.filter((m) => !m.loading && m.content)
			.map((m) => ({ role: m.role, content: m.content }));

		const threadId = botThreadId || `bot_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
		if (!botThreadId) setBotThreadId(threadId);

		const variables = {
			projectId,
			question: message,
			threadId,
			scenarioId: scenarioId || null,
			screen: screen || null,
			language: i18n.language || null,
			history: JSON.stringify(history)
		};

		if (!variables.projectId) delete variables.projectId;
		if (!variables.scenarioId) delete variables.scenarioId;
		if (!variables.language) delete variables.language;

		const result = await apolloClient.query({
			query: askBot,
			variables
		});

		if (callback) callback();
		return;

		setBotThreadMessages(
			[
				...newMessages,
				{
					role: "assistant",
					...result.data.askBot
				}
			]
		);
		if (result.data.askBot.threadId) {
			setBotThreadId(result.data.askBot.threadId);
		}
		if (callback) callback();
	}

	const resetBotThread = () => {
		setBotThreadMessages([]);
		setBotThreadId(null);
	}

	const addEmptyAssistantMessage = () => {
		setBotThreadMessages([...botThreadMessages, {
			role: "assistant",
			loading: true,
			content: "",
		}]);
	}

	const getLastMessage = () => {
		return botThreadMessages[botThreadMessages.length - 1];
	}

	const getMessages = () => {
		return botThreadMessages;
	}

	const onReceiveProjectMessage = (data, projectInfos) => {

		if (!data) return;
		const message = JSON.parse(data.message);
		const projectId = message?.projectId;
		let initialStakeholders = projectInfos.stakeholders || [];

		if (message?.threadId) setBotThreadId(message?.threadId);
		let lastMessage = getLastMessage();

		if (message.event?.data?.type == "message_creation") {
			// If last message is from the user, add a new message
			if (!lastMessage || lastMessage.role == "user") {
				addEmptyAssistantMessage();
			}
		} else if (message.event?.event == "thread.message.delta") {

			if (lastMessage?.role == "assistant") {
				content[message?.event?.counter] = message?.event?.data?.delta?.content.map(c => c.text.value).join('')

				const joined = content.join("").replace(/\【.*?\】/g, '');
				const current = messagesRef.current;
				const updatedLast = { ...current[current.length - 1], content: joined, loading: false };
				const newMessages = [...slice(current, 0, current.length - 1), updatedLast];
				scheduleFlush(newMessages);
			}
		} else if (message.event?.event == "thread.run.completed") {
			content = []
		} else if (message?.type == "situation_analysis") {

			console.log('message', message);

			const objectsToCreate = {
				stakeholders: {
					defaults: StakeholderDefaults,
					action: (values) => {
						const optimizedStakeholder = omit({
							...values,
							...calculateOptimalStakeholderPosition(initialStakeholders, values.action_level, values.action_polarity)
						}, ["action_level", "action_polarity"]);

						initialStakeholders = [...initialStakeholders, optimizedStakeholder];
						createStakeholder(projectId, [], { ...optimizedStakeholder })
					}
				},

				risks: {
					defaults: RiskDefaults,
					action: (values) => createRisk(projectId, [], values)
				},
				stakes: {
					defaults: StakeDefaults,
					action: (values) => createStake(projectId, values)
				},
				indicators: {
					defaults: ConstraintDefaults,
					action: (values) => createIndicator(projectId, values)
				},
				openings: {
					defaults: {
						...RiskDefaults,
						isOpportunity: true
					},
					action: createRisk
				}
			};

			// Bug Marco 2026-05-27: a situation_analysis message with objectType
			// but without items (or with items=undefined) crashed the SPA on the
			// for...of below (Symbol.iterator of undefined). Guard against it.
			// Also guard the objectsToCreate lookup so an unknown objectType
			// doesn't crash later in .action.
			const recipe = message?.objectType ? objectsToCreate[message.objectType] : null;
			if (recipe && Array.isArray(message?.items)) {
				for (const object of message.items) {
					recipe.action(object, recipe.defaults);
				}
			}
		}
	}

	useEffect(() => {
		const parsedPreferences = storage.getJSON<typeof defaultPreferences>(STORAGE_KEYS.PREFERENCES);

		if (parsedPreferences) {
			setPreferences({
				...defaultPreferences,
				...parsedPreferences
			});
		}
		setReady(true);
	}, [storage]);

	const setSpecificPreferences = async (type, preference, value) => {
		const newPreferences = set(preferences, `${type}.${preference}`, value);
		setPreferences({ ...newPreferences });
		storage.setJSON(STORAGE_KEYS.PREFERENCES, newPreferences);
	};

	const select = async (obj, callback) => {
		await setSelection(obj);
		callback && callback();
	};

	const deselect = async (callback) => {
		await setSelection(null);
		callback && callback();
	};

	return <UIContext.Provider value={{
		selection,
		select: select,
		deselect: setSelection,
		preferences,
		setPreferences: setSpecificPreferences,
		botThread: {
			id: botThreadId,
			messages: botThreadMessages
		},

		setBotThreadId,
		addBotMessage,
		resetBotThread,
		onReceiveProjectMessage,

		maskedZones,
		setMaskedZones,

		projectRole,
		setProjectRole,
		isReadOnly,
		canEdit

	}}>
		{ready ? children : null}
	</UIContext.Provider >


}

export default UIProvider;
